import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Warning,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const PTSTariffsBilling = () => (
  <Page
    title="PTS Tariffs and Billing"
    intro="Patient Transport has its own pricing and billing machinery — tariffs that
    price journeys automatically, per-booking quotes and invoices, and billing runs that
    invoice a period's completed journeys in bulk. This page is the finance-team view of
    where that lives; the Patient Transport section covers the detail."
  >
    <Section title="What PTS billing involves">
      <Bullets>
        <Bullet>
          <strong>Tariffs</strong> — price cards set up under{' '}
          <DocLink
            url="/site-configuration/pts-pricing"
            text="Configuration > Patient Transport > Pricing & Tariffs"
          />
          , covering call-out fees, per-mile rates (including base-to-base
          mileage, with a per-journey set-mileage override), waiting time,
          and per-organisation contract rates.
        </Bullet>
        <Bullet>
          <strong>Quotes</strong> — priced from tariffs against a booking, then
          sent, accepted or declined, and converted into invoices.
        </Bullet>
        <Bullet>
          <strong>Invoices</strong> — auto-priced from tariffs and journey
          data, managed at <DocLink url="/pts/invoices" text="PTS Invoicing" />{' '}
          with their own send / mark-paid / credit-note workflow and a
          PTS-specific Xero export.
        </Bullet>
        <Bullet>
          <strong>Billing runs</strong> — batch invoicing of completed journeys
          for an organisation over a period, so regular contract work is
          invoiced in one pass rather than booking by booking.
        </Bullet>
      </Bullets>
      <Typography>
        Everything above is documented step-by-step in{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="PTS Quoting, Invoicing & Billing"
          docsPage
        />{' '}
        and{' '}
        <DocLink
          url="/patient-transport/organisations"
          text="Organisations and Users"
          docsPage
        />{' '}
        (for contract and SLA settings on each organisation).
      </Typography>
    </Section>

    <Section title="How it meets the Finance area">
      <Typography>
        PTS invoices and quotes flow into the combined Finance lists
        automatically — they appear on{' '}
        <DocLink url="/invoices" text="Invoices" /> and{' '}
        <DocLink url="/finance/quotes" text="Quotes" /> with a PTS source chip
        and a link to the booking, and they count towards the Finance Centre
        figures and Finance KPIs.
      </Typography>
      <Typography>
        The combined invoice page carries the full invoice lifecycle for PTS
        invoices — approve, send, mark paid, credit notes, and reissue — and
        opens for PTS Co-Ordinators as well as the Event Planning team. PTS
        Co-Ordinators can edit drafts and send invoices from there; approving,
        payments, credit notes, and reissuing remain Finance-team actions. See{' '}
        <DocLink
          url="/finance/invoices-and-credit-notes"
          text="Invoices and Credit Notes"
          docsPage
        />{' '}
        for the lifecycle in full.
      </Typography>
      <Info>
        Draft invoices can be edited (line items, dates, reference) and
        deleted. Once approved, an invoice is fixed: corrections are made with
        a credit note, and <strong>Reissue</strong> credits the remainder in
        full and creates a new draft copy to amend.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <Typography>
        PTS tariffs, quotes, invoices, and billing runs all require PTS{' '}
        <strong>Co-Ordinator</strong> access on the employee record (set under{' '}
        <DocLink
          url="/site-configuration/pts-access"
          text="Configuration > Patient Transport > PTS Access"
        />
        ) — not the Finance team role.
      </Typography>
      <Warning>
        The Finance team role alone does not open the PTS billing screens, and
        PTS Co-Ordinator access alone does not open the combined Finance lists
        (though it does open individual invoice pages). Staff who handle PTS
        billing end-to-end need both.
      </Warning>
    </Section>
  </Page>
)

export default PTSTariffsBilling
